import React, { useEffect, useState } from 'react';
import './DataCards.css';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Paginator,
    PaginatorCurrentPageReportOptions,
    PaginatorFirstPageLinkOptions,
    PaginatorLastPageLinkOptions,
    PaginatorNextPageLinkOptions,
    PaginatorPrevPageLinkOptions,
    PaginatorRowsPerPageDropdownOptions
} from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import LoadingPage from '../../LoadingPage';
import { Message } from 'primereact/message';
import ChevronLink from '../../common/ChevronLink/ChevronLink';
import { URLKeys } from '../../../Constants';

type Props<T> = {
    renderCardCallback: (data: T) => React.ReactElement;
    getCardUrl: (data: T) => string;
    getUniqueId: (data: T) => number;
    cardsData: T[];
    id?: string;
    totalResultText?: string;
    loading?: boolean;
};

const DataCards = <T,>({
    renderCardCallback,
    getCardUrl,
    cardsData,
    getUniqueId,
    id,
    totalResultText,
    loading = false
}: Props<T>) => {
    const { t } = useTranslation(['translation']);
    const [searchParams, setSearchParams] = useSearchParams();
    const [lastVisitedId, setLastVisitedId] = useState<number>();
    const [numberOfDisplayedRows, setNumberOfDisplayedRows] = useState(Number(searchParams.get(URLKeys.rows)) || 10);
    const [currentPageFirstIndex, setCurrentPageFirstIndex] = useState(
        Number(searchParams.get(URLKeys.page)) * numberOfDisplayedRows || 0
    );

    const onPageChange = (event: { first: number; rows: number }) => {
        setCurrentPageFirstIndex(event.first);
        setNumberOfDisplayedRows(event.rows);

        const pageNumber = event.first / event.rows + 1;

        setSearchParams(
            (prevParams) => {
                const updatedSearchParams = new URLSearchParams(prevParams);

                if (pageNumber !== 1) {
                    updatedSearchParams.set(URLKeys.page, pageNumber.toString());
                } else {
                    updatedSearchParams.delete(URLKeys.page);
                }

                if (event.rows !== 10) {
                    updatedSearchParams.set(URLKeys.rows, event.rows.toString());
                } else {
                    updatedSearchParams.delete(URLKeys.rows);
                }

                return updatedSearchParams;
            },
            // replace + preventScrollReset to avoid scrollToTop; going through setSearchParams
            // (instead of a raw window.history.replaceState) keeps the router's own search params
            // state in sync, so it doesn't get clobbered by other components that also write to the URL.
            { replace: true, preventScrollReset: true }
        );
    };

    const paginatorTemplate = {
        layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown',
        PrevPageLink: ({ className, onClick, disabled }: PaginatorPrevPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>chevron_left</span>
                </button>
            );
        },
        NextPageLink: ({ className, onClick, disabled }: PaginatorNextPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>chevron_right</span>
                </button>
            );
        },
        FirstPageLink: ({ className, onClick, disabled }: PaginatorFirstPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>first_page</span>
                </button>
            );
        },
        LastPageLink: ({ className, onClick, disabled }: PaginatorLastPageLinkOptions) => {
            return (
                <>
                    <button type='button' className={className} onClick={onClick} disabled={disabled}>
                        <span className='material-symbols-rounded'>last_page</span>
                    </button>
                </>
            );
        },
        CurrentPageReport: ({ currentPage, totalPages }: PaginatorCurrentPageReportOptions) => {
            return <div className='current-page-report'>{`${t('x-outOf-y', { x: currentPage, y: totalPages })}`}</div>;
        },
        RowsPerPageDropdown: ({ value, options, onChange }: PaginatorRowsPerPageDropdownOptions) => {
            return (
                <div className='p-paginator-rowsPerPage'>
                    <span>{t('resultsPerPage')}</span>
                    <Dropdown value={value} options={options} onChange={onChange} />
                </div>
            );
        }
    };

    useEffect(() => {
        const page = Number(searchParams.get(URLKeys.page)) || 1;
        const rows = Number(searchParams.get(URLKeys.rows)) || 10;
        setCurrentPageFirstIndex((page - 1) * rows);
        setNumberOfDisplayedRows(rows);
    }, [searchParams]);

    return (
        <div className='data-cards' id={id}>
            {loading ? (
                <LoadingPage />
            ) : cardsData.length === 0 ? (
                <Message severity='info' text={t('DataCards.NoResultsMessage', { ns: 'translation' })} />
            ) : (
                <>
                    <p className='total-results-text'>
                        {totalResultText
                            ? totalResultText
                            : t('totalResults', {
                                  totalCount: cardsData.length,
                                  count: cardsData.length
                              })}
                    </p>
                    {cardsData
                        .slice(currentPageFirstIndex, currentPageFirstIndex + numberOfDisplayedRows)
                        .map((item, index) => (
                            <Link
                                to={getCardUrl(item)}
                                key={index}
                                onClick={() => setLastVisitedId(getUniqueId(item))}
                                onAuxClick={() => setLastVisitedId(getUniqueId(item))}
                                className={lastVisitedId === getUniqueId(item) ? 'active data-Card' : 'data-Card'}
                            >
                                {renderCardCallback(item)}
                            </Link>
                        ))}
                    <Paginator
                        first={currentPageFirstIndex}
                        rows={numberOfDisplayedRows}
                        template={paginatorTemplate}
                        totalRecords={cardsData.length}
                        pageLinkSize={11}
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={onPageChange}
                    />
                    {currentPageFirstIndex + numberOfDisplayedRows < cardsData.length && (
                        <ChevronLink
                            text={t('loadMore')}
                            chevronDirection='down'
                            linkDisabled
                            onClick={() => setNumberOfDisplayedRows(numberOfDisplayedRows + 10)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default DataCards;
